import { Ban, ChevronRight, LogIn, Undo2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { Clipboard } from '@/components/Clipboard'
import { KeyValueList, StatusBadge } from '@/components/ds'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { AdminAccountSubscriptions } from '@/containers/AdminAccounts/AdminAccountSubscriptions'
import { RoleBadge } from '@/containers/AdminUsers/RoleBadge'
import { AdminAccountFeaturesForm } from '@/forms/AdminAccountFeaturesForm/AdminAccountFeaturesForm'
import { AdminAccountMaxVariantAxesForm } from '@/forms/AdminAccountMaxVariantAxesForm/AdminAccountMaxVariantAxesForm'
import API from '@/lib/api'
import { AdminAccountDetail } from '@/types/Account'

import { AdminSection } from './AdminSection'
import { formatAdminDate, formatAdminDateTime, relativeAge } from './adminFormat'

export function AdminAccountDetailView({
  account,
  onAccountUpdate,
}: {
  account: AdminAccountDetail
  onAccountUpdate: (account: AdminAccountDetail) => void
}) {
  const { t: tUsers } = useTranslation(undefined, {
    keyPrefix: 'admin.users',
  })
  const { t: tAccounts } = useTranslation(undefined, {
    keyPrefix: 'admin.accounts',
  })
  const { t: tAbandoned } = useTranslation(undefined, {
    keyPrefix: 'admin.accounts.abandoned',
  })
  const navigate = useNavigate()

  const [updating, setUpdating] = useState(false)
  const [impersonating, setImpersonating] = useState(false)

  const onToggleAbandoned = () => {
    setUpdating(true)

    API.patch(`/api/v2/admin/accounts/${account.id}`, {
      abandoned: !account.abandoned,
    })
      .then((response) => {
        toast.success(tAbandoned(account.abandoned ? 'unmarkSuccess' : 'markSuccess'))
        onAccountUpdate(response.data)
      })
      .catch(() => {
        toast.error(tAbandoned('error'))
      })
      .finally(() => {
        setUpdating(false)
      })
  }

  const onImpersonate = () => {
    setImpersonating(true)
    API.post(`/api/v2/admin/accounts/${account.id}/impersonate`)
      .then(() => {
        window.location.href = '/'
      })
      .catch(() => {
        toast.error(tAccounts('list.actions.impersonateError'))
        setImpersonating(false)
      })
  }

  const enabledFeatures = account.features.filter((feature) => feature.enabled).length
  const owner = account.ownerId

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-3">
            <h1 className="truncate text-2xl font-medium tracking-[-0.02em]">{account.name}</h1>
            {account.abandoned ? (
              <StatusBadge status="draft" label={tAbandoned('badge')} />
            ) : (
              <StatusBadge status="paid" label="Aktivní" />
            )}
          </div>
          <Clipboard value={account.id}>
            <span className="font-mono text-xs text-muted-foreground">{account.id}</span>
          </Clipboard>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" disabled={updating} onClick={onToggleAbandoned}>
            {account.abandoned ? <Undo2 /> : <Ban />}
            {account.abandoned ? tAbandoned('unmark') : tAbandoned('mark')}
          </Button>
          <Button disabled={impersonating} onClick={onImpersonate}>
            <LogIn />
            {tAccounts('list.actions.impersonate')}
          </Button>
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
        <aside className="space-y-6">
          <AdminSection title="Údaje">
            <KeyValueList
              items={[
                { label: 'Vlastník', value: owner?.fullName ?? '-' },
                { label: 'E-mail', value: owner?.email ?? '-' },
                { label: 'Poslední doklad', value: formatAdminDate(account.lastDocumentCreatedAt) },
                { label: 'Poslední změna', value: formatAdminDateTime(account.updatedAt) },
                { label: 'Uživatelé', value: account.bindings.length },
                { label: 'Funkce', value: `${enabledFeatures} z ${account.features.length}` },
              ]}
            />
            {account.lastDocumentCreatedAt && (
              <p className="text-xs text-muted-foreground">Naposledy vystavil doklad {relativeAge(account.lastDocumentCreatedAt)}.</p>
            )}
          </AdminSection>

          <AdminSection title="Funkce" description="Zapnuté moduly účtu.">
            <div className="rounded-lg border p-4">
              <AdminAccountFeaturesForm
                key={account.features.map((feature) => `${feature.id}:${feature.enabled}`).join(',')}
                accountId={account.id}
                features={account.features}
                onSuccess={onAccountUpdate}
              />
            </div>
          </AdminSection>

          <AdminSection title={tAccounts('maxVariantAxes.title')}>
            <div className="rounded-lg border p-4">
              <AdminAccountMaxVariantAxesForm
                key={account.maxVariantAxes}
                accountId={account.id}
                maxVariantAxes={account.maxVariantAxes}
                onSuccess={onAccountUpdate}
              />
            </div>
          </AdminSection>
        </aside>

        <div className="min-w-0 space-y-8">
          <AdminAccountSubscriptions accountId={account.id} />

          <AdminSection title="Uživatelé" description={`${account.bindings.length} s přístupem k účtu`}>
            <div className="overflow-hidden rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Uživatel</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {account.bindings.map((binding) => {
                    const isOwner = owner && String(owner.id) === String(binding.userId)
                    return (
                      <TableRow
                        key={binding.id}
                        onClick={() => navigate(`/admin/users/${binding.userId}`)}
                        className="group cursor-pointer"
                      >
                        <TableCell className="max-w-0">
                          {isOwner ? (
                            <>
                              <p className="truncate font-medium">{owner.fullName || owner.email}</p>
                              <p className="truncate text-xs text-muted-foreground">{owner.email}</p>
                            </>
                          ) : (
                            <p className="font-mono text-xs">{binding.userId}</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {binding.roles.map((role) => (
                              <RoleBadge key={role}>{tUsers(`roles.${role}`)}</RoleBadge>
                            ))}
                            {!binding.verified && <RoleBadge muted>{tUsers('list.state.unverified')}</RoleBadge>}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <ChevronRight className="ml-auto size-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </AdminSection>
        </div>
      </div>
    </div>
  )
}
